import Api from "@ciq/shared/api/api"
import { DatasourceColumn } from "../models/DatasourceColumn"
import { AttributeSort } from "@ciq/shared/utils/sort"
import { DATASET_TYPE_HOURLY, DATASET_TYPE_SEMESTER } from "../map_consts"
import { getGeoApi, getPath } from "./utils"
import { CampusData } from "../models/CampusData"
import {
  getDatasourceData,
  getResourceData,
  hasCampusFlowData,
  hasDatasourceData,
  hasHourlyData,
  hasResourceData,
  hasSemesterData,
  isCampusFlowStale,
  isDatasourceStale,
  isHourlyStale,
  isResourceStale,
  isSemesterStale,
  setCampusFlowData,
  setDatasourceData,
  setHourlyData,
  setResourceData,
  setSemesterData,
} from "./store"

/**
 * Creates a recursive iterator function that yields all descendant zones for a given zone.
 * The returned function traverses the zone hierarchy depth-first, yielding children at each level.
 *
 * @param {Object} lookup - Zone parent-child lookup object where keys are parent zone IDs and values are arrays of child zones
 * @returns {function(Object): Array<Object>} Function that takes a zone and returns array of all descendant zones
 */
export const buildSubZoneIter = lookup => {
  return zone => {
    function* handler(zones) {
      if (zones == null) {
        return
      }

      for (const child of zones) {
        yield child

        for (const innerChild of handler(lookup[child.id])) {
          yield innerChild
        }
      }
    }

    return Array.from(handler(lookup[zone.id]))
  }
}

/**
 * Parses resource API response and builds CampusData.
 *
 * @param {Object} response - API response object containing campus resource data
 * @returns {CampusData} Parsed resource data with zone hierarchy lookups
 */
export const parseResourceResponse = response => {
  return CampusData.fromJson(response.data)
}

/**
 * @typedef {Object} ColumnGroup
 * @property {string} group - The group name
 * @property {Array<DatasourceColumn>} columns - Array of columns in this group
 */

/**
 * @typedef {Object} DatasourceTableConfig
 * @property {string} datasourceId - The unique identifier for the datasource
 * @property {string} datasetId - The unique identifier for the dataset
 * @property {Array<ColumnGroup>} groups - Column groups sorted by group name
 * @property {Array<DatasourceColumn>} columns - All columns in the table
 * @property {boolean} hasColumns - Boolean indicating if there are enabled columns
 * @property {Array<DatasourceColumn>} enabledColumns - Array of enabled columns only
 * @property {Array<DatasourceColumn>} mapEnabledColumns - Array of map-enabled columns only
 */

/**
 * Extracts and processes a dataset configuration by dataset_type from datasource response.
 * Groups columns by their group property and separates enabled and map-enabled columns.
 *
 * @param {Object} response - API response object containing datasource datasets
 * @param {string} datasetType - The dataset_type identifier (e.g. "semester_summary", "semester_hourly")
 * @returns {DatasourceTableConfig} Processed table configuration with grouped and filtered columns
 * @throws {Error} If no dataset with the specified type is found
 * @throws {Error} If multiple datasets with the same type are found
 */
export const getDataSourceTable = (response, datasetType) => {
  const filtered = response.data.datasets.filter(ds => ds.dataset_type === datasetType)

  const data = filtered
    .map(ds => {
      const groupLookup = {}
      const groups = []
      const columns = []
      const enabledColumns = []
      const mapEnabledColumns = []

      for (const column of ds.columns) {
        const columnData = DatasourceColumn.fromJson(column)
        columns.push(columnData)

        if (!column.enabled) {
          continue
        }

        enabledColumns.push(columnData)

        if (columnData.mapEnabled) {
          mapEnabledColumns.push(columnData)
        }

        let groupIndex = groupLookup[columnData.group]
        if (groupIndex == null) {
          groups.push({ group: columnData.group, columns: [] })
          groupIndex = groups.length - 1
          groupLookup[columnData.group] = groupIndex
        }

        groups[groupIndex].columns.push(columnData)
      }

      return {
        datasourceId: ds.datasource.id,
        datasetId: ds.id,
        groups: groups.sort(AttributeSort("group")),
        columns,
        hasColumns: enabledColumns.length > 0,
        enabledColumns: enabledColumns,
        mapEnabledColumns,
      }
    })

  if (data.length === 0) {
    throw new Error(`Unable to find dataset type '${datasetType}'`)
  }

  if (data.length > 1) {
    throw new Error(`Found multiple datasets for type '${datasetType}'`)
  }

  return data[0]
}

/**
 * @typedef {Object} ColorRange
 * @property {number} min - Minimum threshold value
 * @property {number} max - Maximum threshold value
 * @property {string} label - Display label for this range
 * @property {string} color - Hex color code for this range
 */

/**
 * @typedef {Object} ColorRanges
 * @property {Array<ColorRange>} percentage - Color ranges for percentage type (5 levels)
 * @property {Array<ColorRange>} default - Color ranges for default type (4 levels)
 */

/**
 * @typedef {Object} ParsedDatasourceData
 * @property {Array<any>} datasets - Array of dataset configurations
 * @property {Array<any>} groups - Array of datasource groups
 * @property {DatasourceTableConfig} semesterTable - Semester summary table configuration with columns and enabled columns
 * @property {DatasourceTableConfig} hourlyTable - Hourly summary table configuration with columns and enabled columns
 * @property {ColorRanges} colorRanges - Color range configurations from API
 */

/**
 * Parses datasource API response and extracts table configurations.
 * Identifies and processes semester and hourly summary tables from the datasource datasets.
 *
 * @param {Object} response - API response object containing datasource configuration
 * @returns {ParsedDatasourceData} Parsed datasource data with table configurations
 * @throws {Error} If colorRanges is missing from response (API contract violation)
 */
export const parseDatasourceResponse = response => {
  if (!response.data.colorRanges) {
    throw new Error("API contract violation: datasource response missing colorRanges")
  }

  return {
    datasets: response.data.datasets,
    groups: response.data.groups,
    semesterTable: getDataSourceTable(response, DATASET_TYPE_SEMESTER),
    hourlyTable: getDataSourceTable(response, DATASET_TYPE_HOURLY),
    colorRanges: response.data.colorRanges,
  }
}

/**
 * @typedef {Object} ParsedSemesterData
 * @property {Object} zoneData - Zone data with structure {'zone-id': {'column': value}}
 * @property {Object} columnData - Column data with structure {'column': {'zone-id': maxValue}}
 */

/**
 * Transforms raw semester API data into zoneData and columnData structures.
 * Parses API response format {'zone-id': {'column': value}} into both zone-indexed
 * and column-indexed views of the data. Handles null and NaN values by skipping them.
 * Uses Math.max to resolve duplicate zone/column pairs.
 *
 * @param {CampusData} resourceData -
 * @param {ParsedDatasourceData} datasourceData
 * @returns {ParsedSemesterData} Object containing both zoneData and columnData structures
 */
export function parseSemesterResponse(resourceData, datasourceData) {
  return response => {
    const metricEnabledColumns = datasourceData.semesterTable.enabledColumns
    const mapEnabledColumns = datasourceData.semesterTable.mapEnabledColumns

    const enabledColumns = new Set()
    for (const column of metricEnabledColumns) {
      enabledColumns.add(column.name)
    }

    for (const column of mapEnabledColumns) {
      enabledColumns.add(column.name)
    }

    const zoneData = {}
    const maxValues = {}

    for (const [zoneId, attributeData] of Object.entries(response.data)) {
      const zone = resourceData.getZoneById(zoneId)
      if (!zone) {
        continue
      }

      for (const [column, value] of Object.entries(attributeData)) {
        if (!enabledColumns.has(column)) {
          continue
        }

        zoneData[zoneId] ??= {}
        zoneData[zoneId][column] = value

        if (resourceData.smallestZoneIds.has(zoneId)) {
          maxValues[column] ??= 1
          if (value == null || isNaN(value)) {
            continue
          }

          maxValues[column] = Math.max(maxValues[column], value)
        }
      }
    }

    return { zoneData, maxValues }
  }
}

/**
 * @typedef {Object} ParsedHourlyData
 * @property {Object} zoneData - Zone-indexed hourly data with structure: {zoneId: {column: {day: {hour: value}}}}
 * @property {Object} columnData - Column-indexed data with structure: {column: maxValue}
 */

/**
 * Creates a function that parses hourly API response.
 * The returned function transforms raw hourly data into both zone-indexed and column-indexed structures.
 * Handles null and NaN values by skipping them. Uses Math.max to resolve duplicate zone/column pairs.
 *
 * @param {CampusData} resourceData -
 * @param {ParsedDatasourceData} datasourceData - Datasource configuration with column definitions
 * @returns {function(Object): ParsedHourlyData} Function that parses hourly response data
 */
export function parseHourlyResponse(resourceData, datasourceData) {
  return response => {
    const enabledColumnNames = new Set()
    for (const column of datasourceData.hourlyTable.columns) {
      if (!(column.enabled || column.mapEnabled)) {
        continue
      }

      enabledColumnNames.add(column.name)
    }

    const byDayData = {}
    const byHourData = {}
    const maxValues = {}

    for (const [zoneId, dayData] of Object.entries(response.data)) {
      const zone = resourceData.getZoneById(zoneId)
      if (!zone) {
        continue
      }

      for (const [day, attributes] of Object.entries(dayData)) {
        for (const [column, values] of Object.entries(attributes)) {
          if (!enabledColumnNames.has(column) || column === "hours" || !Array.isArray(values)) {
            continue
          }

          for (let hour = 0; hour < 24; hour++) {
            const hourIndex = attributes.hours.findIndex(h => h === hour)
            if (hourIndex === -1) {
              continue
            }

            const value = attributes[column][hourIndex]
            if (value == null || isNaN(value)) {
              continue
            }

            if (resourceData.smallestZoneIds.has(zoneId)) {
              maxValues[column] ??= 1
              maxValues[column] = Math.max(maxValues[column], value)
            }

            const dayNum = Number(day)

            byDayData[zoneId] ??= {}
            byDayData[zoneId][column] ??= {}
            byDayData[zoneId][column][dayNum] ??= {}
            byDayData[zoneId][column][dayNum][hour] = value

            byHourData[zoneId] ??= {}
            byHourData[zoneId][column] ??= {}
            byHourData[zoneId][column][hour] ??= {}
            byHourData[zoneId][column][hour][dayNum] = value
          }
        }
      }
    }

    return { byDayData, byHourData, maxValues }
  }
}

/**
 * Fetches and stores map data based on what's needed in cache.
 * Orchestrates parallel API calls and stores results in IndexedDB.
 *
 * This is an async generator that yields progress messages for the worker to post.
 * Automatically checks IndexedDB cache using has* functions to determine what data
 * needs fetching. Yields messages progressively as data loads, parsed, and stored.
 *
 * @param {Object} params - Fetch parameters
 * @param {string} params.schoolId - The school identifier (required)
 * @param {string} params.semesterId - The semester identifier
 *
 * @yields {{type: string, payload: Object}} Progress messages in this sequence:
 *
 * **Validation Error (if schoolId missing):**
 * ```javascript
 * { type: "RESOURCE_ERROR", payload: { error: "schoolId is required" } }
 * ```
 *
 * **All Cached (no fetching needed):**
 * ```javascript
 * { type: "RESOURCE_LOADED", payload: {
 *   schoolId: "schoolId",
 *   semesterId: "semesterId",
 *   fetchedDatasourceData: false,
 *   fetchedResourceData: false,
 *   fetchedSemesterData: false,
 *   fetchedHourlyData: false,
 *   fetchedCampusFlowData: false
 * }}
 * ```
 *
 * **Normal Flow (fetching required):**
 * ```javascript
 * // 1. Loading started
 * { type: "RESOURCE_LOADING", payload: { loading: true } }
 *
 * // 2. Success - data fetched and stored
 * { type: "RESOURCE_LOADED", payload: {
 *   schoolId: "schoolId",
 *   semesterId: "semesterId",
 *   fetchedDatasourceData: true,  // true if this data was fetched
 *   fetchedResourceData: true,
 *   fetchedSemesterData: false,   // false if already cached
 *   fetchedHourlyData: true,
 *   fetchedCampusFlowData: false   // true if campus flow was fetched
 * }}
 *
 * // 3. Loading finished
 * { type: "RESOURCE_LOADING", payload: { loading: false } }
 * ```
 *
 * **Error Flow:**
 * ```javascript
 * // 1. Loading started
 * { type: "RESOURCE_LOADING", payload: { loading: true } }
 *
 * // 2. Error occurred
 * { type: "RESOURCE_ERROR", payload: {
 *   error: "API request failed",  // error message
 *   handled: undefined            // optional handled flag
 * }}
 *
 * // 3. Loading finished (always sent in finally block)
 * { type: "RESOURCE_LOADING", payload: { loading: false } }
 * ```
 *
 * @returns {AsyncGenerator<{type: string, payload: Object}, void, undefined>}
 *
 * @example
 * // In worker event listener:
 * case "FETCH":
 *   for await (const message of fetchAndStoreMapData(payload)) {
 *     self.postMessage(message)
 *   }
 *   break
 */
export async function* fetchAndStoreMapData({
  schoolId,
  semesterId,
}) {
  // Validate required parameters
  if (!schoolId) {
    yield {
      type: "RESOURCE_ERROR",
      payload: { error: "schoolId is required" },
    }
    return
  }

  // Check what data exists in cache
  const hasCachedDatasource = await hasDatasourceData(schoolId)
  const hasCachedResource = await hasResourceData(schoolId)
  const hasCachedSemester = await hasSemesterData(schoolId, semesterId)
  const hasCachedHourly = await hasHourlyData(schoolId, semesterId)
  const hasCachedCampusFlow = await hasCampusFlowData(schoolId, semesterId)

  // Check staleness - data is stale if cached timestamp differs from backend timestamp
  // Debouncing is built into isStale checks to prevent excessive API calls
  const datasourceStale = hasCachedDatasource && (await isDatasourceStale(schoolId))
  const resourceStale = hasCachedResource && (await isResourceStale(schoolId))
  const semesterStale =
    hasCachedSemester && (await isSemesterStale(schoolId, semesterId))
  const hourlyStale = hasCachedHourly && (await isHourlyStale(schoolId, semesterId))
  const campusFlowStale = hasCachedCampusFlow && (await isCampusFlowStale(schoolId, semesterId))

  // Determine what needs fetching
  const needsDatasourceData = !hasCachedDatasource || datasourceStale
  const needsResourceData = !hasCachedResource || resourceStale
  const needsSemesterData =
    !hasCachedSemester || semesterStale || needsDatasourceData || needsResourceData
  const needsHourlyData =
    !hasCachedHourly || hourlyStale || needsDatasourceData || needsResourceData
  // Campus flow is independent - no cascade from datasource/resource changes
  const needsCampusFlowData = !hasCachedCampusFlow || campusFlowStale

  // If all data is cached and fresh, no need to fetch
  if (!needsDatasourceData && !needsResourceData && !needsSemesterData && !needsHourlyData && !needsCampusFlowData) {
    yield {
      type: "RESOURCE_LOADED",
      payload: {
        schoolId,
        semesterId,
        fetchedDatasourceData: false,
        fetchedResourceData: false,
        fetchedSemesterData: false,
        fetchedHourlyData: false,
        fetchedCampusFlowData: false,
      },
    }
    return
  }

  // Stale-while-revalidate: If we have stale data, notify that we're using it
  const hasStaleData =
    (hasCachedDatasource && datasourceStale) ||
    (hasCachedResource && resourceStale) ||
    (hasCachedSemester && semesterStale) ||
    (hasCachedHourly && hourlyStale) ||
    (hasCachedCampusFlow && campusFlowStale)

  if (hasStaleData) {
    yield {
      type: "RESOURCE_STALE",
      payload: {
        schoolId,
        semesterId,
        message: "Using cached data, refreshing in background",
      },
    }
  }

  // Signal loading started
  yield {
    type: "RESOURCE_LOADING",
    payload: { loading: true },
  }

  try {
    // Fetch and store data
    const geoApi = await getGeoApi(schoolId)
    const stores = []

    let datasourceData = null
    if (needsDatasourceData) {
      datasourceData = await Api.get(`schools/${schoolId}/map/datasource`).then(
        parseDatasourceResponse
      )
      stores.push(setDatasourceData(schoolId, datasourceData))
    }

    let resourceData = null
    if (needsResourceData) {
      resourceData = await geoApi
        .get(getPath({ schoolId, table: "data" }))
        .then(parseResourceResponse)
      stores.push(setResourceData(schoolId, resourceData))
    }

    if (needsSemesterData) {
      if (resourceData == null) {
        resourceData = await getResourceData(schoolId)
      }

      if (datasourceData == null) {
        datasourceData = await getDatasourceData(schoolId)
      }

      stores.push(
        geoApi
          .get(getPath({ schoolId, semesterId, table: "semester_summary" }))
          .then(parseSemesterResponse(resourceData, datasourceData))
          .then(data => setSemesterData(schoolId, semesterId, data))
      )
    }

    if (needsHourlyData) {
      if (resourceData == null) {
        resourceData = await getResourceData(schoolId)
      }

      if (datasourceData == null) {
        datasourceData = await getDatasourceData(schoolId)
      }

      stores.push(
        geoApi
          .get(getPath({ schoolId, semesterId, table: "facility_usage_hourly" }))
          .then(parseHourlyResponse(resourceData, datasourceData))
          .then(data => setHourlyData(schoolId, semesterId, data))
      )
    }

    // Campus flow is optional - not all schools have this data
    // 404 responses are handled gracefully (data simply not available)
    // Pushed to stores[] for parallel execution (avoids unhandled rejections if this throws)
    if (needsCampusFlowData) {
      stores.push(
        geoApi
          .get(getPath({ schoolId, semesterId, table: "campus_flow" }))
          .then(response => setCampusFlowData(schoolId, semesterId, response.data))
          .catch(error => {
            // 404 is expected for schools without campus flow data
            if (error?.response?.status === 404) return
            throw error
          })
      )
    }

    await Promise.all(stores)

    // Signal loading completed successfully
    yield {
      type: "RESOURCE_LOADED",
      payload: {
        schoolId,
        semesterId,
        fetchedDatasourceData: needsDatasourceData,
        fetchedResourceData: needsResourceData,
        fetchedSemesterData: needsSemesterData,
        fetchedHourlyData: needsHourlyData,
        fetchedCampusFlowData: needsCampusFlowData,
      },
    }
  } catch (error) {
    yield {
      type: "RESOURCE_ERROR",
      payload: {
        error: error.message,
        handled: error.handled,
      },
    }
  } finally {
    // Signal loading finished (whether success or error)
    yield {
      type: "RESOURCE_LOADING",
      payload: { loading: false },
    }
  }
}

/**
 * Web Worker for loading resource data off the main thread.
 * Handles fetching and parsing campus resource data asynchronously.
 * Implements intelligent caching using IndexedDB to avoid refetching school-level data when only semester data changes.
 *
 * ## Architecture
 *
 * This worker uses an **async generator pattern** (`fetchAndStoreMapData`) that yields progress messages
 * as data loads. The worker simply iterates over yielded messages and posts them to the main thread:
 *
 * ```javascript
 * case "FETCH":
 *   for await (const message of fetchAndStoreMapData(payload)) {
 *     self.postMessage(message)
 *   }
 *   break
 * ```
 *
 * This pattern provides:
 * - **Progressive Updates**: Messages sent as each stage completes (validation → cache check → loading → complete)
 * - **Error Handling**: Errors caught within generator and yielded as messages
 * - **Testability**: Generator can be tested independently of worker infrastructure
 * - **Simplicity**: Worker becomes a thin messaging wrapper (3 lines of code)
 *
 * ## Cache Strategy
 *
 * The generator internally checks IndexedDB cache using `has*` functions to determine what needs fetching:
 * - `hasDatasourceData(schoolId)` - Check if datasource config is cached
 * - `hasResourceData(schoolId)` - Check if campus resources are cached
 * - `hasSemesterData(schoolId, semesterId)` - Check if semester summary is cached
 * - `hasHourlyData(schoolId, semesterId)` - Check if hourly data is cached
 *
 * **Intelligent Fetching**:
 * - School-level data (resources, datasources) cached separately from semester data
 * - Switching semesters only fetches new semester-specific data
 * - All API calls made in parallel using `Promise.all()`
 * - Results parsed and stored in IndexedDB before success message sent
 *
 * ## Storage
 *
 * - **Persistent Caching**: IndexedDB for cross-session data persistence
 * - **Automatic Parsing**: Semester/hourly data parsed into structured formats before storing
 * - **Off-Main-Thread**: All parsing happens in worker to maintain UI responsiveness
 *
 * ## Message Protocol
 *
 * ### Incoming Messages
 *
 * **FETCH**: Initiates data fetch
 * ```javascript
 * {
 *   type: "FETCH",
 *   payload: {
 *     schoolId: "school-123",        // Required
 *     semesterId: "semester-456"
 *   }
 * }
 * ```
 *
 * ### Outgoing Messages
 *
 * **RESOURCE_LOADING**: Loading state change
 * ```javascript
 * { type: "RESOURCE_LOADING", payload: { loading: true|false } }
 * ```
 *
 * **RESOURCE_LOADED**: Data successfully fetched and stored
 * ```javascript
 * {
 *   type: "RESOURCE_LOADED",
 *   payload: {
 *     schoolId: "schoolId",
 *     semesterId: "semesterId",
 *     fetchedDatasourceData: true|false,  // true if newly fetched
 *     fetchedResourceData: true|false,
 *     fetchedSemesterData: true|false,
 *     fetchedHourlyData: true|false,
 *     fetchedCampusFlowData: true|false
 *   }
 * }
 * ```
 *
 * **RESOURCE_ERROR**: Error occurred
 * ```javascript
 * {
 *   type: "RESOURCE_ERROR",
 *   payload: {
 *     error: "Error message",
 *     handled: undefined  // Optional handled flag
 *   }
 * }
 * ```
 *
 * ## IndexedDB Structure
 *
 * - **Database**: "admin-app-db" (shared across application)
 * - **map_datasources**: `{ schoolId, data }` - Datasource configuration per school
 * - **map_resources**: `{ schoolId, data }` - Campus resources per school
 * - **map_semesters**: `{ [schoolId, semesterId], data }` - Semester summary data
 * - **map_hourly**: `{ [schoolId, semesterId], data }` - Hourly utilization data
 * - **map_campus_flow**: `{ [schoolId, semesterId], data }` - Campus flow route data
 *
 * See `fetchAndStoreMapData()` JSDoc for detailed message flow examples.
 */
self.addEventListener("message", async event => {
  const { type, payload } = event.data

  switch (type) {
    case "FETCH":
      for await (const message of fetchAndStoreMapData(payload)) {
        self.postMessage(message)
      }
      break

    default:
      // console.warn(`Resource worker received unknown message type: ${type}`)
      break
  }
})
